import { useState } from 'react';
import {
	add,
	DataClassification,
	DataClassificationSchema,
} from '@/api/doc/data-classification';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '@/components/ui/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CirclePlus } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function AddDataClassification() {
	const [isSheetOpen, setIsSheetOpen] = useState(false);
	const queryClient = useQueryClient();

	const form = useForm<DataClassification>({
		resolver: zodResolver(DataClassificationSchema),
		defaultValues: {
			id: null,
			name: '',
			slug: '',
			deletedOn: null,
		},
		mode: 'all',
	});

	const { mutate } = useMutation({
		mutationFn: (dataClassification: DataClassification) =>
			add(dataClassification),
		onSuccess: () => {
			toast.success(
				`Success! The data classification has been successfully added`,
				{
					dismissible: true,
				},
			);
			setIsSheetOpen(false);
			queryClient.invalidateQueries({ queryKey: ['data-classifications'] });
		},
		onError: () => {
			toast.error(`Error! The data classification could not be added`, {
				dismissible: true,
			});
		},
	});
	const onSubmit: SubmitHandler<DataClassification> = (
		values: DataClassification,
	) => mutate(values);

	form.watch();

	return (
		<Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
			<SheetTrigger asChild>
				<Button>
					<CirclePlus />
					Add Data Classification
				</Button>
			</SheetTrigger>
			<SheetContent className="overflow-scroll">
				<SheetHeader>
					<SheetTitle>Add Data Classification</SheetTitle>
				</SheetHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Data Classification Name</FormLabel>
									<FormControl>
										<Input {...field} placeholder="Data classification name" />
									</FormControl>
									<FormDescription>Name the data classification</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							disabled={!form.formState.isValid || form.formState.isSubmitting}
						>
							{form.formState.isSubmitting ? 'Adding...' : 'Add'}
						</Button>
					</form>
				</Form>
			</SheetContent>
		</Sheet>
	);
}
